import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Download, ChevronLeft, ChevronRight, Loader2, ShieldAlert,
  Smartphone, Monitor, TrendingUp, TrendingDown, Users, MapPin,
} from "lucide-react";

/* ------------------------------------------------ shared bits */
const PctBar = ({ pct }) => (
  <div className="flex items-center gap-2 min-w-[120px]">
    <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--cx-surface))] overflow-hidden">
      <div className="h-full rounded-full bg-[hsl(var(--cx-navy))]" style={{ width: `${Math.min(pct, 100)}%` }} />
    </div>
    <span className="tabular text-xs font-semibold w-11 text-right">{pct}%</span>
  </div>
);

const DeviceIcon = ({ type }) =>
  type === "mobile" ? <Smartphone size={13} className="inline mr-1 text-muted-foreground" />
    : <Monitor size={13} className="inline mr-1 text-muted-foreground" />;

const downloadBlob = async (url, params, fallbackName) => {
  const r = await api.get(url, { params, responseType: "blob" });
  const obj = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = obj;
  a.download = fallbackName;
  a.click();
  URL.revokeObjectURL(obj);
};

/* Loading / empty / data states without nested ternaries. */
const LogResults = ({ loading, data }) => {
  if (loading) {
    return <div className="p-4 space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={`la-log-skeleton-${i}`} className="h-9" />)}</div>;
  }
  if (!data?.items?.length) {
    return <EmptyState title="No login events match these filters" />;
  }
  return (
    <div className="overflow-x-auto">
      <Table className="cx-table min-w-[900px]" data-testid="activity-log-table">
        <TableHeader>
          <TableRow>
            <TableHead className="pl-4">Date & Time (IST)</TableHead>
            <TableHead>User</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Function</TableHead>
            <TableHead>Device</TableHead>
            <TableHead>IP Address</TableHead>
            <TableHead className="pr-4">Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((ev) => (
            <TableRow key={ev.id} data-testid={`activity-row-${ev.id}`}>
              <TableCell className="pl-4 tabular whitespace-nowrap">{ev.at_fmt}</TableCell>
              <TableCell className="max-w-[200px]">
                <span className="font-medium">{ev.user_name || "(unknown account)"}</span>
                <span className="block text-[0.65rem] text-muted-foreground truncate">{ev.email}</span>
              </TableCell>
              <TableCell className="whitespace-nowrap">{ev.role_label || "—"}</TableCell>
              <TableCell className="whitespace-nowrap">{ev.location}</TableCell>
              <TableCell>{ev.function_scope || "—"}</TableCell>
              <TableCell className="whitespace-nowrap capitalize"><DeviceIcon type={ev.device_type} />{ev.device_type}</TableCell>
              <TableCell className="tabular text-xs">{ev.ip}</TableCell>
              <TableCell className="pr-4 whitespace-nowrap">
                {ev.success ? (
                  <Badge variant="outline" className="text-[hsl(var(--status-green))] border-[hsl(var(--status-green)/0.4)]">Success</Badge>
                ) : (
                  <Badge variant="outline" className="text-[hsl(var(--cx-crimson))] border-[hsl(var(--cx-crimson)/0.4)]">Failed</Badge>
                )}
                {ev.failed_flag && (
                  <Badge className="ml-1.5 bg-[hsl(var(--cx-crimson))] text-white border-transparent" data-testid={`failed-flag-${ev.id}`}>
                    <ShieldAlert size={11} className="mr-1" />5+ fails today
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/* ------------------------------------------------ Tab 1: Activity Log */
const ActivityLogTab = ({ roles }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    user_id: "all", role: "all", location: "all", status: "all",
    date_from: "", date_to: "",
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => console.error("Users fetch failed:", e));
  }, []);

  const queryParams = useCallback(() => {
    const q = { page, page_size: 50 };
    if (filters.user_id !== "all") q.user_id = filters.user_id;
    if (filters.role !== "all") q.role = filters.role;
    if (filters.location !== "all") q.location = filters.location;
    if (filters.status !== "all") q.status = filters.status;
    if (filters.date_from) q.date_from = filters.date_from;
    if (filters.date_to) q.date_to = filters.date_to;
    return q;
  }, [filters, page]);

  useEffect(() => {
    setLoading(true);
    api.get("/login-activity", { params: queryParams() })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [queryParams]);

  useEffect(() => { setPage(1); }, [filters]);

  const doExport = async () => {
    setExporting(true);
    try {
      await downloadBlob("/login-activity/export",
        { ...queryParams(), page: undefined, page_size: undefined },
        "login_activity.xlsx");
      toast.success("Login activity exported");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const set = (k) => (v) => setFilters((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4">
      <Card className="p-3">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          <Select value={filters.user_id} onValueChange={set("user_id")}>
            <SelectTrigger className="h-9" data-testid="activity-filter-user-select"><SelectValue placeholder="User" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.role} onValueChange={set("role")}>
            <SelectTrigger className="h-9" data-testid="activity-filter-role-select"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {roles.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.location} onValueChange={set("location")}>
            <SelectTrigger className="h-9" data-testid="activity-filter-location-select"><SelectValue placeholder="Location" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {(data?.locations || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={set("status")}>
            <SelectTrigger className="h-9" data-testid="activity-filter-status-select"><SelectValue placeholder="Result" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Success + failed</SelectItem>
              <SelectItem value="success">Success only</SelectItem>
              <SelectItem value="failed">Failed only</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" className="h-9" value={filters.date_from}
            onChange={(e) => set("date_from")(e.target.value)} data-testid="activity-filter-date-from" />
          <Input type="date" className="h-9" value={filters.date_to}
            onChange={(e) => set("date_to")(e.target.value)} data-testid="activity-filter-date-to" />
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b bg-[hsl(var(--cx-surface))]">
          <p className="text-xs text-muted-foreground tabular">
            {data ? `${data.total.toLocaleString()} login events · system-generated, read-only` : "…"}
          </p>
          <Button variant="outline" size="sm" onClick={doExport} disabled={exporting} data-testid="activity-export-button">
            {exporting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Download size={14} className="mr-1.5" />}
            Export Excel
          </Button>
        </div>
        <LogResults loading={loading} data={data} />
        {data && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t bg-[hsl(var(--cx-surface))]">
            <p className="text-xs text-muted-foreground tabular">Page {data.page} of {data.pages}</p>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="activity-prev-page"><ChevronLeft size={15} /></Button>
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="activity-next-page"><ChevronRight size={15} /></Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ------------------------------------------------ Tab 2: Adoption Summary */
const AdoptionTab = () => {
  const [data, setData] = useState(null);
  const [view, setView] = useState("daily");

  useEffect(() => {
    api.get("/login-activity/adoption").then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!data) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={`la-adoption-skeleton-${i}`} className="h-16" />)}</div>;

  const rows = view === "daily" ? data.daily : data.weekly;
  const trendUp = data.trend >= 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card data-testid="adoption-week-tile">
          <CardContent className="pt-5">
            <p className="text-[0.65rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">{data.week_label} — logged in</p>
            <p className="font-serif text-3xl mt-1">{data.this_week.pct}%</p>
            <p className="text-xs text-muted-foreground tabular">{data.this_week.logged_in} of {data.total_active} active users</p>
          </CardContent>
        </Card>
        <Card data-testid="adoption-trend-tile">
          <CardContent className="pt-5">
            <p className="text-[0.65rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">Trend vs last week</p>
            <p className={`font-serif text-3xl mt-1 flex items-center gap-2 ${trendUp ? "text-[hsl(var(--status-green))]" : "text-[hsl(var(--cx-crimson))]"}`}>
              {trendUp ? <TrendingUp size={22} /> : <TrendingDown size={22} />}{trendUp ? "+" : ""}{data.trend} pts
            </p>
            <p className="text-xs text-muted-foreground tabular">last week: {data.last_week_pct}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <p className="text-[0.65rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">Active users</p>
            <p className="font-serif text-3xl mt-1">{data.total_active}</p>
            <p className="text-xs text-muted-foreground">across all roles & locations</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="font-serif text-base font-normal">Users logged in vs total active</CardTitle>
          <div className="flex rounded-md border overflow-hidden">
            {["daily", "weekly"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`adoption-view-${v}`}
                className={`px-3 py-1 text-xs font-medium capitalize ${view === v ? "bg-[hsl(var(--cx-navy))] text-white" : "hover:bg-[hsl(var(--cx-surface))]"}`}>
                {v}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-1.5" data-testid="adoption-trend-rows">
            {rows.map((r) => (
              <div key={r.date || r.week_start} className="flex items-center gap-3">
                <span className="text-xs w-24 shrink-0 text-muted-foreground">{r.label}</span>
                <div className="flex-1"><PctBar pct={r.pct} /></div>
                <span className="text-xs tabular text-muted-foreground w-16 text-right">{r.logged_in}/{r.total}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="adoption-by-role-panel">
          <CardHeader className="pb-2"><CardTitle className="font-serif text-base font-normal flex items-center gap-1.5"><Users size={15} /> By role — this week</CardTitle></CardHeader>
          <CardContent className="pt-1 space-y-1.5">
            {data.by_role.map((r) => (
              <div key={r.role} className="flex items-center gap-3">
                <span className="text-xs w-40 shrink-0 truncate">{r.role}</span>
                <div className="flex-1"><PctBar pct={r.pct} /></div>
                <span className="text-xs tabular text-muted-foreground w-12 text-right">{r.logged_in}/{r.total}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card data-testid="adoption-by-location-panel">
          <CardHeader className="pb-2"><CardTitle className="font-serif text-base font-normal flex items-center gap-1.5"><MapPin size={15} /> By location — this week</CardTitle></CardHeader>
          <CardContent className="pt-1 space-y-1.5">
            {data.by_location.map((r) => (
              <div key={r.location} className="flex items-center gap-3">
                <span className="text-xs w-40 shrink-0 truncate">{r.location}</span>
                <div className="flex-1"><PctBar pct={r.pct} /></div>
                <span className="text-xs tabular text-muted-foreground w-12 text-right">{r.logged_in}/{r.total}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/* ------------------------------------------------ Tab 3: Inactive Users */
const InactiveTab = () => {
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState(7);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    api.get("/login-activity/inactive").then((r) => {
      setData(r.data);
      setThreshold(r.data.threshold_days);
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveThreshold = async () => {
    setSaving(true);
    try {
      await api.put("/config/adoption", { inactive_days: Number(threshold) });
      toast.success(`Inactive threshold set to ${threshold} days`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      await downloadBlob("/login-activity/inactive/export", {}, "inactive_users.xlsx");
      toast.success("Inactive users exported");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  if (!data) return <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={`la-inactive-skeleton-${i}`} className="h-16" />)}</div>;

  return (
    <div className="space-y-4">
      <Card className="p-3 flex flex-wrap items-center gap-3">
        <p className="text-sm">
          <strong className="font-serif text-xl tabular" data-testid="inactive-count">{data.count}</strong>
          <span className="text-muted-foreground"> of {data.total_active} users inactive {data.threshold_days}+ days</span>
        </p>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Threshold (days)</span>
          <Input type="number" min={1} max={90} className="h-8 w-20" value={threshold}
            onChange={(e) => setThreshold(e.target.value)} data-testid="inactive-threshold-input" />
          <Button size="sm" variant="outline" onClick={saveThreshold} disabled={saving} data-testid="inactive-threshold-save">
            {saving ? <Loader2 size={13} className="animate-spin" /> : "Apply"}
          </Button>
          <Button size="sm" variant="outline" onClick={doExport} disabled={exporting} data-testid="inactive-export-button">
            {exporting ? <Loader2 size={13} className="animate-spin mr-1" /> : <Download size={13} className="mr-1" />}Export
          </Button>
        </div>
      </Card>

      {data.groups.length === 0 ? (
        <EmptyState title="Everyone is active" subtitle={`No users inactive for ${data.threshold_days}+ days.`} />
      ) : (
        data.groups.map((g) => (
          <Card key={g.location} className="overflow-hidden" data-testid={`inactive-group-${g.location}`}>
            <div className="px-4 py-2 border-b bg-[hsl(var(--cx-surface))] flex items-center gap-2">
              <MapPin size={13} className="text-[hsl(var(--cx-navy))]" />
              <span className="text-sm font-medium">{g.location}</span>
              <Badge variant="outline" className="ml-auto text-[0.65rem]">{g.users.length} user{g.users.length !== 1 ? "s" : ""}</Badge>
            </div>
            <Table className="cx-table">
              <TableBody>
                {g.users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="pl-4 font-medium">{u.name}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell className="pr-4 text-right text-xs text-muted-foreground tabular">{u.last_seen_fmt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ))
      )}
    </div>
  );
};

/* ------------------------------------------------ page */
export default function LoginActivityPage() {
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    api.get("/meta").then((r) => setRoles(r.data.roles || [])).catch((e) => console.error("Meta fetch failed:", e));
  }, []);

  return (
    <div data-testid="login-activity-page">
      <PageHeader
        title="Login Activity"
        subtitle="System-generated usage telemetry · read-only · no one can edit or delete login records"
      />
      <Tabs defaultValue="log">
        <TabsList className="mb-4">
          <TabsTrigger value="log" data-testid="tab-activity-log">Activity Log</TabsTrigger>
          <TabsTrigger value="adoption" data-testid="tab-adoption-summary">Adoption Summary</TabsTrigger>
          <TabsTrigger value="inactive" data-testid="tab-inactive-users">Inactive Users</TabsTrigger>
        </TabsList>
        <TabsContent value="log"><ActivityLogTab roles={roles} /></TabsContent>
        <TabsContent value="adoption"><AdoptionTab /></TabsContent>
        <TabsContent value="inactive"><InactiveTab /></TabsContent>
      </Tabs>
    </div>
  );
}
